import { AfterViewInit, Component, HostListener, Input, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CourseCatalogPostingStream } from 'app/library/course-catalog-landing-page/models/course-catalog-posting-stream.model';
import { CourseCatalogService } from 'app/library/course-catalog/services/course-catalog.service';
import { Posting } from 'app/library/posting/models/posting.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { GeneralAnalyticsService } from 'app/services/general-analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { GetUserExtendedAttributeValuePipe } from 'app/projects/shared/src/lib/pipes/getUserExtendedAttributeValue.pipe';
import { CatalogOfferingsDialogComponent } from '../../dialogs/catalog-offerings-dialog/catalog-offerings-dialog.component';
import { CustomOfferingPropertyChip } from './interfaces/custom-offering-property-chip.interface';

@Component({
    selector: 'app-course-catalog-posting-card',
    templateUrl: './course-catalog-posting-card.component.html',
    styleUrls: ['./course-catalog-posting-card.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class CourseCatalogPostingCardComponent extends UnsubscribeOnDestroy implements AfterViewInit {
    @Input() postingResource: CourseCatalogPostingStream;
    readonly maxVisibleCustomOfferingProperties = 3;

    @Input() isInsideDialog: boolean = false;
    @Input() isLast: boolean = false;
    @Input() isFirst: boolean = false;
    @Input() index: number = 0;
    @Input() hoverPosition: 'left' | 'center' | 'right' | 'auto' = 'auto';

    isMobileView: boolean;
    currentSlideWrapper: HTMLElement;

    @HostListener('window:resize')
    getScreenSize(): void {
        // Align with Fuse gt-md / desktop layout (1280px): hover on laptops (incl. 1366), click on tablets
        const scrWidth = window.innerWidth;
        this.isMobileView = scrWidth < 1280;
    }

    onMouseEnter(posting: CourseCatalogPostingStream, event: MouseEvent) {
        // Show slide hover active data only for desktop view
        if (!this.isMobileView || this.isInsideDialog) {
            const slideWrapper = event.currentTarget as HTMLElement;
            setTimeout(() => {
                if (this.currentSlideWrapper === slideWrapper) {
                    slideWrapper.classList.add('slide-hover-active');
                }
            }, 500);
        }
    }

    onMouseLeave(event: MouseEvent) {
        const slideWrapper = event.currentTarget as HTMLElement;
        slideWrapper.classList.remove('slide-hover-active');
        this.currentSlideWrapper = null;
    }

    onMouseMove(event: MouseEvent) {
        const slideWrapper = event.currentTarget as HTMLElement;
        this.currentSlideWrapper = slideWrapper;
    }

    @Input()
    featuredWidget: CourseCatalogPostingStream;
    arrayOfViewedElements: string[] = [];

    constructor(
        private _router: Router,
        private _locationBasedAnalyticsService: GeneralAnalyticsService,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _getUserExtendedAttributeValuePipe: GetUserExtendedAttributeValuePipe,
        private _courseCatalogService: CourseCatalogService
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.getScreenSize();
    }

    get hoverPositionClass(): string | null {
        if (this.hoverPosition === 'auto') {
            return null;
        }

        return `slide-hover-${this.hoverPosition}`;
    }

    openPostingUrl(posting: CourseCatalogPostingStream) {
        // Follow posting url on click only if it's not mobile view
        if (!this.isMobileView) {
            // If the url starts with the same domain as the custom domain (whole one, not just base domain) then open internally
            if (posting.url.includes(window.location.host)) {
                const relativePath = new URL(posting.url).pathname;

                if (posting.kind === 'course_category') {
                    const categoryId = relativePath.split('/').filter(Boolean).pop();
                    if (categoryId) {
                        this._courseCatalogService.setSelectedCategoryId(categoryId);
                    }
                }

                this._router.navigateByUrl(relativePath);
            } else {
                const currentPostUrl = posting.url;
                window.open(currentPostUrl, '_blank');
            }
        }
    }

    openPostInDialog() {
        // Open posting in dialog only for mobile view
        if (this.isMobileView) {
            const dialogRef = this._matDialog.open(CatalogOfferingsDialogComponent, {
                width: '656px',
            });

            dialogRef.componentInstance.postingResource = this.postingResource;
        }
    }

    async sendPostingViewEvent(eventName: string, posting: Posting) {
        if (this._router.url === '/grow') {
            this._locationBasedAnalyticsService.createPostingEvent(
                eventName,
                posting.id,
                posting.postingType.id,
                posting.translation.title,
                null,
                this.featuredWidget.id,
                this.featuredWidget.translation.title
            );
        }
    }

    get customOfferingPropertyChips(): CustomOfferingPropertyChip[] {
        const attributes = this.postingResource?.courseOffering?.attributes ?? [];
        if (!attributes.length) {
            return [];
        }

        return attributes
            .filter((attributeWithValue) => attributeWithValue.attribute.visible)
            .map((attributeWithValue) => {
                const value = this._getUserExtendedAttributeValuePipe.transform(attributeWithValue.attribute, attributes);
                const normalizedValue = value !== null && value !== undefined ? `${value}`.trim() : '';

                if (!normalizedValue) {
                    return null;
                }

                return {
                    label: this._translateService.instant(attributeWithValue.attribute.translationKey),
                    value: normalizedValue,
                };
            })
            .filter(Boolean);
    }

    get visibleCustomOfferingPropertyChips(): CustomOfferingPropertyChip[] {
        return this.customOfferingPropertyChips.slice(0, this.maxVisibleCustomOfferingProperties);
    }

    get hiddenCustomOfferingPropertyChips(): CustomOfferingPropertyChip[] {
        return this.customOfferingPropertyChips.slice(this.maxVisibleCustomOfferingProperties);
    }

    get hiddenCustomOfferingPropertyCount(): number {
        return this.hiddenCustomOfferingPropertyChips.length;
    }

    get hiddenCustomOfferingPropertiesTooltip(): string {
        return this.hiddenCustomOfferingPropertyChips.map((chip) => `${chip.label}: ${chip.value}`).join('\n');
    }

}
